// src/forms/html_element_helpers/custom_fields/custom_fields_renderer.js
//
// Renders validated `data-hub-form-fields` rows (output of parse_and_validate_form_fields)
// into the same form DOM shape the legacy buildFormContent produces: identical form/row/
// wrapper classes, identifier auto-add/auto-require, submit auto-append, "Not you?" reset.
// Rows are authored — no width auto-chunking (flex-wrap handles overflow).

import {
    cell_phone_names,
    at_least_one_required_form_identifier_fields,
    user_data_form_field_placeholders,
} from '../../../helpers/static_vars';
import { generate_sms_optin_intl_tel } from '../../../tel/telephone_sms_inputs';
import { get_tailwind_size_class } from '../form_styling';
import {
    render_input_field,
    create_submit_button,
    create_not_you_reset_p,
} from '../form_generators';
import { fields_json_contains_type } from './fields_schema';

const ROW_CLASSES = ['flex', 'flex-wrap', '-mx-2', 'mb-4', 'add-el-sm-mb-0'];

const DISPLAY_TEXT_TAG_CLASSES = {
    p: ['text-md', 'mb-2', 'data-hub-custom-display-text'],
    // h1 matches the legacy form-title look (text-primary-brand-color is
    // runtime-generated by inject_styles from theme_json, like the legacy h1).
    h1: ['text-3xl', 'font-bold', 'text-primary-brand-color', 'mb-2'],
    h2: ['text-2xl', 'font-bold', 'mb-2'],
    h3: ['text-xl', 'font-semibold', 'mb-2'],
};

const METADATA_MARKER_ATTRIBUTE = 'data-hub-form-metadata-field';
const CUSTOM_DATA_MARKER_ATTRIBUTE = 'data-hub-form-custom-data-field';
// Declared-type marker for hidden values — read by harvest_marked_fields so authored
// boolean/number hidden values arrive typed in metadata/custom_data POST bodies.
const VALUE_TYPE_MARKER_ATTRIBUTE = 'data-hub-value-type';
const PLACEHOLDER_WORD_OVERRIDES = {
    id: 'ID',
    ip: 'IP',
    sms: 'SMS',
    url: 'URL',
    utc: 'UTC',
    uuid: 'UUID',
    zip: 'ZIP',
};

const create_row_div = () => {
    const row_div = document.createElement('div');
    row_div.classList.add(...ROW_CLASSES);
    return row_div;
};

const create_field_wrapper_div = (width_class) => {
    const wrapper_div = document.createElement('div');
    wrapper_div.classList.add('px-2', width_class, 'add-el-sm-w-full');
    return wrapper_div;
};

/** Set the metadata / custom_data marker attribute per data_target (person = no marker). */
const apply_data_target_marker = (input, data_target) => {
    if (data_target === 'metadata') {
        input.setAttribute(METADATA_MARKER_ATTRIBUTE, 'true');
    } else if (data_target === 'custom_data') {
        input.setAttribute(CUSTOM_DATA_MARKER_ATTRIBUTE, 'true');
    }
};

const format_data_key_placeholder = (name) => String(name || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => {
        const lower = word.toLowerCase();
        if (PLACEHOLDER_WORD_OVERRIDES[lower]) return PLACEHOLDER_WORD_OVERRIDES[lower];
        if (/^\d+$/.test(word)) return word;
        return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join(' ');

const get_text_like_placeholder = (field) => {
    if (typeof field.placeholder === 'string' && field.placeholder.trim() !== '') {
        return field.placeholder;
    }
    if (field.data_target === 'person') {
        return user_data_form_field_placeholders[field.name] || format_data_key_placeholder(field.name);
    }
    return field.placeholder;
};

const render_display_text_field = (field) => {
    const wrapper_div = create_field_wrapper_div(get_tailwind_size_class(field.width));
    const text_element = document.createElement(field.tag);
    text_element.classList.add(...(DISPLAY_TEXT_TAG_CLASSES[field.tag] || DISPLAY_TEXT_TAG_CLASSES.p));
    // textContent, never innerHTML — display text is XSS-inert by construction.
    text_element.textContent = field.text;
    wrapper_div.appendChild(text_element);
    return wrapper_div;
};

const render_text_like_field = (field, allow_prefill) => {
    // Prefill dataset is person-only: metadata/custom_data inputs must never be
    // auto-filled from the visitor's stored person data.
    const effective_allow_prefill = allow_prefill && field.data_target === 'person';
    const input_div = render_input_field(
        {
            name: field.name,
            size: get_tailwind_size_class(field.width),
            required: field.required,
            input_type: field.type,
            placeholder: get_text_like_placeholder(field),
        },
        effective_allow_prefill
    );
    const input = input_div.querySelector('input');
    apply_data_target_marker(input, field.data_target);

    // Deliberately NO generated id on inputs — the harvest key is `id || name` in both
    // get_metadata_form_fields and the user_data collection loop, so we associate the
    // label implicitly by wrapping instead.
    if (field.label) {
        const label_element = document.createElement('label');
        label_element.classList.add('block', 'w-full');
        const label_text_span = document.createElement('span');
        label_text_span.classList.add('block', 'text-sm', 'mb-1', 'data-hub-custom-field-label');
        label_text_span.textContent = field.label;
        label_element.appendChild(label_text_span);
        label_element.appendChild(input);
        input_div.appendChild(label_element);
    }
    return input_div;
};

const render_checkbox_field = (field, allow_prefill) => {
    const wrapper_div = create_field_wrapper_div(get_tailwind_size_class(field.width));

    const label_element = document.createElement('label');
    label_element.classList.add('flex', 'flex-row', 'items-start', 'gap-2', 'w-full', 'cursor-pointer');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.name = field.name;
    input.classList.add(
        'w-5',
        'h-5',
        'shrink-0',
        'mt-0.5',
        'accent-primary-brand-color',
        'border',
        'border-grey-brand-color-400',
        'rounded'
    );
    if (field.required) {
        input.required = true;
    }
    // Prefill dataset is person-only, mirroring render_text_like_field.
    if (allow_prefill && field.data_target === 'person') {
        input.dataset.allowDataHubPrefill = 'true';
    }
    if (field.data_target !== 'person') {
        // Marked-field harvesters submit the boolean checked state directly.
        apply_data_target_marker(input, field.data_target);
    }
    // Person-target checkboxes rely on the existing collection path:
    // parse_boolean_falsy_types(element.checked) → user_data (whitelisted names only).

    const label_text_span = document.createElement('span');
    label_text_span.classList.add('text-sm', 'flex-1', 'min-w-0', 'data-hub-custom-checkbox-label');
    label_text_span.textContent = field.label;

    label_element.appendChild(input);
    label_element.appendChild(label_text_span);
    wrapper_div.appendChild(label_element);
    return wrapper_div;
};

const render_hidden_field = (field) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = field.name;
    input.value = String(field.value);
    apply_data_target_marker(input, field.data_target);
    // Typed hidden lane: only declared boolean/number values on metadata/custom_data
    // targets get the type marker (person values ride the user_data whitelist lane,
    // which has its own typing — they stay strings here).
    if (field.data_target !== 'person' && ['boolean', 'number'].includes(typeof field.value)) {
        input.setAttribute(VALUE_TYPE_MARKER_ATTRIBUTE, typeof field.value);
    }
    return input;
};

/**
 * Build a form from validated data-hub-form-fields rows.
 *
 * @param {Object} params
 * @param {Array<Array<Object>>} params.rows - validated output of parse_and_validate_form_fields.
 * @param {boolean} params.allow_prefill
 * @param {string} params.form_submit_button_text
 * @param {Object} params.data_hub_instance
 * @param {string} [params.sms_optin_label_text]
 * @param {Function} [params.error_function]
 * @returns {{form: HTMLFormElement, submit_button: HTMLElement|null}}
 */
export const build_form_from_fields_json = ({
    rows,
    allow_prefill,
    form_submit_button_text,
    data_hub_instance,
    sms_optin_label_text,
    error_function = window?.data_hub?.error_function,
}) => {
    const form = document.createElement('form');
    form.classList.add('data_hub_form');
    form.setAttribute('role', 'form');

    const set_of_form_inputs_added = new Set();
    // Pre-scan: when an sms_optin block exists, skip person-target tel fields named in
    // cell_phone_names — the ITI phone input comes from the sms block (legacy dedupe parity).
    const bool_show_sms_optin = fields_json_contains_type(rows, 'sms_optin');

    let authored_submit_button = null;

    // AUTHORED person-target identifier inputs, collected during render. The identifier
    // auto-add/auto-require pass runs over THIS list only — never a form-wide input query,
    // which would catch the sms_optin block's internal cell_phone input and force-require
    // it, hard-blocking submits (legacy only considers authored rows).
    const authored_person_identifier_inputs = [];
    const collect_person_identifier_input = (field, rendered_element) => {
        if (field.data_target !== 'person') return;
        if (!at_least_one_required_form_identifier_fields.includes(field.name)) return;
        const input = rendered_element?.matches?.('input')
            ? rendered_element
            : rendered_element?.querySelector?.('input');
        if (input) authored_person_identifier_inputs.push(input);
    };

    const render_field = (field) => {
        switch (field.type) {
            case 'display_text':
                return render_display_text_field(field);
            case 'text':
            case 'email':
            case 'tel':
                if (
                    bool_show_sms_optin
                    && field.type === 'tel'
                    && field.data_target === 'person'
                    && cell_phone_names.includes(field.name)
                ) {
                    return null; // phone input is rendered by the sms_optin block
                }
                set_of_form_inputs_added.add(field.name);
                {
                    const text_like_element = render_text_like_field(field, allow_prefill);
                    collect_person_identifier_input(field, text_like_element);
                    return text_like_element;
                }
            case 'checkbox': {
                set_of_form_inputs_added.add(field.name);
                const checkbox_element = render_checkbox_field(field, allow_prefill);
                collect_person_identifier_input(field, checkbox_element);
                return checkbox_element;
            }
            case 'hidden': {
                set_of_form_inputs_added.add(field.name);
                const hidden_input = render_hidden_field(field);
                collect_person_identifier_input(field, hidden_input);
                return hidden_input;
            }
            case 'group': {
                const wrapper_div = create_field_wrapper_div(get_tailwind_size_class(field.width));
                const card_div = document.createElement('div');
                card_div.classList.add('border', 'border-grey-brand-color-400', 'rounded-md', 'p-4', 'mb-2', 'w-full');
                // One inner flex-wrap row holds ALL children — child widths apply within the card.
                const inner_row_div = document.createElement('div');
                inner_row_div.classList.add('flex', 'flex-wrap', '-mx-2');
                field.children.forEach(child => {
                    const child_element = render_field(child);
                    if (child_element) inner_row_div.appendChild(child_element);
                });
                card_div.appendChild(inner_row_div);
                wrapper_div.appendChild(card_div);
                return wrapper_div;
            }
            case 'submit': {
                const submit_button = create_submit_button(
                    field.text || form_submit_button_text,
                    get_tailwind_size_class(field.width)
                );
                set_of_form_inputs_added.add('submit');
                if (!authored_submit_button) {
                    authored_submit_button = submit_button;
                }
                return submit_button;
            }
            case 'sms_optin': {
                const sms_optin_block = generate_sms_optin_intl_tel(
                    allow_prefill,
                    set_of_form_inputs_added,
                    form,
                    data_hub_instance,
                    sms_optin_label_text
                );
                if (sms_optin_block) {
                    set_of_form_inputs_added.add('sms_optin');
                    set_of_form_inputs_added.add('cell_phone');
                }
                return sms_optin_block || null;
            }
            default:
                // Validated rows should never reach here.
                error_function?.('data-hub-form-fields renderer: unknown field type ' + field.type);
                return null;
        }
    };

    rows.forEach(row => {
        const row_div = create_row_div();
        row.forEach(field => {
            const field_element = render_field(field);
            if (field_element) row_div.appendChild(field_element);
        });
        form.appendChild(row_div);
    });

    // Identifier rule parity with buildFormContent: only the AUTHORED person-target
    // identifier inputs collected during render count (metadata/custom_data inputs never
    // satisfy the identifier requirement, and the sms_optin block's internal cell_phone
    // input must never be force-required).
    const person_identifier_inputs = authored_person_identifier_inputs;

    let fallback_submit_button = null;
    if (person_identifier_inputs.length === 0) {
        // No person identifier authored: append the legacy default email row
        // (email:3/4 required + submit:1/4 when no submit was authored; email:full otherwise).
        const fallback_row = create_row_div();
        const email_size = authored_submit_button ? 'w-full' : 'w-3/4';
        fallback_row.appendChild(render_input_field({ name: 'email', size: email_size, required: true }, allow_prefill));
        if (!authored_submit_button) {
            fallback_submit_button = create_submit_button(form_submit_button_text, 'w-1/4');
            fallback_row.appendChild(fallback_submit_button);
            set_of_form_inputs_added.add('submit');
        }
        set_of_form_inputs_added.add('email');
        form.appendChild(fallback_row);
    } else {
        const has_required_or_prefilled_identifier = person_identifier_inputs.some(
            input => input.required || input.value.trim()
        );
        if (!has_required_or_prefilled_identifier) {
            person_identifier_inputs.forEach(input => {
                input.required = true;
            });
        }
    }

    // If no submit button was authored (or added with the fallback email row), append one.
    let auto_appended_submit_button = null;
    if (!set_of_form_inputs_added.has('submit')) {
        auto_appended_submit_button = create_submit_button(form_submit_button_text);
        form.appendChild(auto_appended_submit_button);
    }

    // Append a "Not you?" reset text
    form.appendChild(create_not_you_reset_p());

    const submit_button_to_return = authored_submit_button
        || fallback_submit_button
        || auto_appended_submit_button
        || form.querySelector('button[name="submit_button"]')
        || null;

    return { form, submit_button: submit_button_to_return };
};
